/**
 * `reportsApi` — RTK Query surface for the reports feature (design §10).
 *
 * Injected onto the shared `apiSlice` (fetchBaseQuery at `/api` + Bearer JWT +
 * single-flight refresh); the feature never touches the store. Browser paths
 * mirror §10.2: `/api/v1/reports/…` hits issue-reports.
 *
 * Attach/detach call the projects-service paths (`/api/projects/…`) through
 * THIS slice rather than `projectsApi.createResource`: the projects feature's
 * `ProjectResourceType` union predates the `report-project` value added
 * server-side in P3-T7 and is owned by another task, so reports keeps its own
 * typed endpoints here (documented deviation, same HTTP contract — §12).
 *
 * Key handling (P6): `createEnvironment` / `rotateEnvironmentKey` return the
 * plaintext `apiKey` inside the mutation result; consumers `.unwrap()` it into
 * React state for the display-once modal and call the hook's `reset()` to drop
 * the cache entry. Nothing persists the key.
 */

import { apiSlice } from '@/store/apiSlice';
import { invalidateResource } from '@/features/permissions/can';
import { PROJECT_RESOURCE } from '@/features/permissions/model';
import type { DataEnvelope } from '@/features/orgs/types';

import type {
  EnvironmentView,
  ProvisionedEnvironment,
  ReportProjectView,
  ReportSearchPage,
  ReportStatus,
  ReportSummaryView,
  SubProjectResourceRow,
} from './types';
import { REPORT_PROJECT_RESOURCE_TYPE } from './types';

/**
 * Envelope-aware unwrap (P3-T9 defect fix).
 *
 * Strips ONLY a genuine single-key envelope — `{ data: ... }`, which is what
 * projects-service hand-writes on `/projects/:id/resources`
 * (`services/projects/src/project/project.controller.ts:209`). Everything
 * issue-reports returns is bare (`services/issue-reports/src/**` registers no
 * response interceptor), so it takes the pass-through branch.
 *
 * The previous rule ("any top-level `data` key") treated the `@cluster/common`
 * cursor page — which carries BOTH `data` and `pagination` — as an envelope and
 * deleted the rows, so the browser always rendered "No reports yet". A payload
 * with more than one key is never treated as an envelope.
 */
function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw as T | undefined;
  }
  const keys = Object.keys(raw as object);
  if (keys.length === 1 && keys[0] === 'data') {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T;
}

/** Cursor page normalizer: keeps `data` + `pagination` (never unwraps them). */
function toSearchPage(
  raw: DataEnvelope<ReportSearchPage> | ReportSearchPage | undefined,
): ReportSearchPage {
  const page = unwrap<ReportSearchPage>(raw);
  const rows = page?.data;
  const pagination = page?.pagination;
  return {
    data: Array.isArray(rows) ? rows : [],
    pagination: {
      cursor: pagination?.cursor ?? null,
      hasMore: pagination?.hasMore ?? false,
      ...(pagination && pagination.totalCount !== undefined
        ? { totalCount: pagination.totalCount }
        : {}),
    },
  };
}

/**
 * P1 §14.5 pattern (mirrors projectsApi.trackProjectMutation): a 403 means the
 * cached verdict was wrong — drop every decision about that resource so the
 * control re-evaluates against the server's answer.
 */
function invalidatePermissionOnFailure(resource: string) {
  return async (_args: unknown, { queryFulfilled }: { queryFulfilled: PromiseLike<unknown> }) => {
    try {
      await queryFulfilled;
    } catch {
      invalidateResource(resource);
    }
  };
}

/** §11.1 class-scoped resource strings (kept in sync with types.ts). */
const REPORTS_PROJECTS_RESOURCE = 'reports:projects';
const REPORTS_ENVIRONMENTS_RESOURCE = 'reports:environments';

export interface ReportSearchArgs {
  reportProjectId: string;
  status?: ReportStatus;
  q?: string;
  cursor?: string;
  /**
   * Cache-busting nonce for the Refresh button (§9.7: re-running the search
   * re-mints the ~5-min presigns). NEVER sent to the server — the query only
   * serializes status/q/cursor into params.
   */
  nonce?: number;
}

const reportsTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: [
    'ReportProjectList',
    'ReportSummary',
    'EnvironmentList',
    'ReportSearch',
    'SubProjectResources',
  ],
});

export const reportsApi = reportsTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * `GET /v1/reports/projects?projectId=` — caller's report projects (names + picker set).
     * Contract: bare `ReportProjectView[]` (`report-projects.service.ts:65`) → pass-through.
     */
    getReportProjects: builder.query<ReportProjectView[], string | void>({
      query: (projectId) => ({
        url: 'v1/reports/projects',
        params: projectId ? { projectId } : undefined,
      }),
      transformResponse: (raw: DataEnvelope<ReportProjectView[]> | ReportProjectView[]) =>
        unwrap<ReportProjectView[]>(raw) ?? [],
      providesTags: (_result, _error, projectId) => [
        { type: 'ReportProjectList', id: String(projectId ?? 'all') },
      ],
    }),

    /**
     * `POST /v1/reports/projects` `{name, projectId?}` (A1 confirmed in the Zod schema).
     * Contract: bare `ReportProjectView` (201) → pass-through.
     */
    createReportProject: builder.mutation<
      ReportProjectView | undefined,
      { name: string; projectId?: string }
    >({
      query: (body) => ({ url: 'v1/reports/projects', method: 'POST', body }),
      transformResponse: (raw: DataEnvelope<ReportProjectView> | ReportProjectView) =>
        unwrap<ReportProjectView>(raw),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ReportProjectList', id: String(projectId ?? 'all') },
      ],
      onQueryStarted: invalidatePermissionOnFailure(REPORTS_PROJECTS_RESOURCE),
    }),

    /**
     * `GET /v1/reports/projects/:id/summary` — panel + detail-header counts.
     * Contract: bare `{ id, environments, reports, activeKnownIssues }`
     * (`reports-search.service.ts:107`) → pass-through (multi-key, never unwrapped).
     */
    getReportProjectSummary: builder.query<ReportSummaryView | undefined, string>({
      query: (reportProjectId) => ({ url: `v1/reports/projects/${reportProjectId}/summary` }),
      transformResponse: (raw: DataEnvelope<ReportSummaryView> | ReportSummaryView) =>
        unwrap<ReportSummaryView>(raw),
      providesTags: (_result, _error, reportProjectId) => [
        { type: 'ReportSummary', id: reportProjectId },
      ],
    }),

    /**
     * `GET /v1/reports/projects/:id/reports/search?status=&q=&cursor=` —
     * limit stays at the server default (20); never auto-invalidated: presigns
     * must remain per-response fresh (§10.1). `nonce` only forks cache entries.
     *
     * Contract: the bare cursor page `{ data, pagination }`
     * (`reports-search.service.ts:153` / `buildCursorPage`) — `data` is the row
     * array, so it must NOT be treated as an envelope (see `unwrap`).
     */
    searchReports: builder.query<ReportSearchPage, ReportSearchArgs>({
      query: ({ reportProjectId, status, q, cursor }) => ({
        url: `v1/reports/projects/${reportProjectId}/reports/search`,
        params: {
          ...(status ? { status } : {}),
          ...(q ? { q } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      transformResponse: (raw: DataEnvelope<ReportSearchPage> | ReportSearchPage | undefined) =>
        toSearchPage(raw),
      providesTags: (_result, _error, args) => [
        {
          type: 'ReportSearch',
          id: `${args.reportProjectId}|${args.status ?? ''}|${args.q ?? ''}|${args.cursor ?? ''}`,
        },
      ],
    }),

    /**
     * `GET /v1/reports/projects/:id/environments` — prefix-only rows.
     * Contract: bare `EnvironmentView[]` (`environments.service.ts:155`) → pass-through.
     */
    listEnvironments: builder.query<EnvironmentView[], string>({
      query: (reportProjectId) => ({ url: `v1/reports/projects/${reportProjectId}/environments` }),
      transformResponse: (raw: DataEnvelope<EnvironmentView[]> | EnvironmentView[]) =>
        unwrap<EnvironmentView[]>(raw) ?? [],
      providesTags: (_result, _error, reportProjectId) => [
        { type: 'EnvironmentList', id: reportProjectId },
      ],
    }),

    /**
     * `POST /v1/reports/projects/:id/environments` — returns the bare
     * `{environment, apiKey}` (`environments.service.ts:148`) and passes through
     * un-unwrapped; the plaintext `apiKey` is consumed once by the
     * key modal and the mutation result is then `reset()`.
     */
    createEnvironment: builder.mutation<
      ProvisionedEnvironment | undefined,
      {
        reportProjectId: string;
        name: string;
        duplicateThreshold: number;
        maxResources: Record<string, number>;
      }
    >({
      query: ({ reportProjectId, ...body }) => ({
        url: `v1/reports/projects/${reportProjectId}/environments`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: DataEnvelope<ProvisionedEnvironment> | ProvisionedEnvironment) =>
        unwrap<ProvisionedEnvironment>(raw),
      invalidatesTags: (_result, _error, { reportProjectId }) => [
        { type: 'EnvironmentList', id: reportProjectId },
        { type: 'ReportSummary', id: reportProjectId },
      ],
      onQueryStarted: invalidatePermissionOnFailure(REPORTS_ENVIRONMENTS_RESOURCE),
    }),

    /**
     * `POST /v1/reports/environments/:id/rotate-key` — plaintext returned once.
     * Contract: bare `{ environment, apiKey }` (`environments.service.ts:178`) →
     * pass-through, so the two-key payload (and its `apiKey`) is never unwrapped.
     */
    rotateEnvironmentKey: builder.mutation<
      ProvisionedEnvironment | undefined,
      { environmentId: string; reportProjectId: string }
    >({
      query: ({ environmentId }) => ({
        url: `v1/reports/environments/${environmentId}/rotate-key`,
        method: 'POST',
      }),
      transformResponse: (raw: DataEnvelope<ProvisionedEnvironment> | ProvisionedEnvironment) =>
        unwrap<ProvisionedEnvironment>(raw),
      invalidatesTags: (_result, _error, { reportProjectId }) => [
        { type: 'EnvironmentList', id: reportProjectId },
      ],
      onQueryStarted: invalidatePermissionOnFailure(REPORTS_ENVIRONMENTS_RESOURCE),
    }),

    /**
     * `GET /projects/:id/resources?type=report-project` — the attached set (§6.1 source 1).
     * Contract: projects-service hand-wraps lists as a genuine single-key
     * envelope `{ data: rows }` (`project.controller.ts:209`) → unwrapped by
     * `unwrap`; a bare array is tolerated too.
     */
    getSubProjectResources: builder.query<SubProjectResourceRow[], string>({
      query: (projectId) => ({
        url: `projects/${projectId}/resources`,
        params: { type: REPORT_PROJECT_RESOURCE_TYPE },
      }),
      transformResponse: (raw: DataEnvelope<SubProjectResourceRow[]> | SubProjectResourceRow[]) =>
        unwrap<SubProjectResourceRow[]>(raw) ?? [],
      providesTags: (_result, _error, projectId) => [
        { type: 'SubProjectResources', id: projectId },
      ],
    }),

    /** `POST /projects/:id/resources` `{type:'report-project', refId}` (attach; Q4 confirmed shape). */
    attachSubProject: builder.mutation<unknown, { projectId: string; refId: string }>({
      query: ({ projectId, refId }) => ({
        url: `projects/${projectId}/resources`,
        method: 'POST',
        body: { type: REPORT_PROJECT_RESOURCE_TYPE, refId },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'SubProjectResources', id: projectId },
        { type: 'ReportProjectList', id: projectId },
      ],
      onQueryStarted: invalidatePermissionOnFailure(PROJECT_RESOURCE),
    }),

    /** `DELETE /projects/:id/resources/:resourceId` — detach (P1 endpoint; the report project is kept). */
    detachSubProject: builder.mutation<unknown, { projectId: string; resourceId: string }>({
      query: ({ projectId, resourceId }) => ({
        url: `projects/${projectId}/resources/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'SubProjectResources', id: projectId },
      ],
      onQueryStarted: invalidatePermissionOnFailure(PROJECT_RESOURCE),
    }),
  }),
});

export const {
  useGetReportProjectsQuery,
  useCreateReportProjectMutation,
  useGetReportProjectSummaryQuery,
  useSearchReportsQuery,
  useListEnvironmentsQuery,
  useCreateEnvironmentMutation,
  useRotateEnvironmentKeyMutation,
  useGetSubProjectResourcesQuery,
  useAttachSubProjectMutation,
  useDetachSubProjectMutation,
} = reportsApi;
